import { z } from 'zod';

/** Os filtros do extrato (`ExpensesWalletStatementCategory`): pagamentos, reembolsos, saldo e
 *  estornos. */
export const VollWalletStatementCategorySchema = z.enum([
  'PAYMENTS',
  'REFUNDS',
  'BALANCE',
  'REVERSALS',
]);
export type VollWalletStatementCategory = z.infer<typeof VollWalletStatementCategorySchema>;

/** O tipo do lançamento para o ícone da linha, lido do `eventType` do wallets-api; `other` é um
 *  evento novo do upstream, mostrado com o rótulo dele. `reversal_partial` e `reversal_total` são
 *  os estornos de pagamento que o `reversalKind` do extrato V2 distingue, e só saem para o cliente
 *  que os pede (`GET /voll-wallet/statement?reversals=1`: este enum é estrito, e um bundle antigo
 *  recusaria a página inteira); `reversal` fica para o estorno sem essa informação (retorno de
 *  reembolso, valor novo do upstream) e para quem não pediu. */
export const VollWalletEntryKindSchema = z.enum([
  'pix',
  'card',
  'refund',
  'balance',
  'reversal',
  'reversal_partial',
  'reversal_total',
  'other',
]);
export type VollWalletEntryKind = z.infer<typeof VollWalletEntryKindSchema>;

/** Um lançamento do extrato, como a linha o desenha. O rótulo e o detalhe vêm prontos do
 *  wallets-api (em pt-BR, como no app legado). */
export const VollWalletStatementEntrySchema = z
  .object({
    title: z.string(),
    /** O nome da carteira: o extrato não traz o id dela. */
    wallet: z.string(),
    label: z.string(),
    detail: z.string().nullable(),
    /** Com sinal: negativo sai da carteira. */
    amount: z.number(),
    /** ISO 4217; ausente = real. */
    currency: z.string().length(3).optional(),
    /** "HH:mm" em São Paulo, quando o wallets-api manda mostrar a hora. */
    time: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
    kind: VollWalletEntryKindSchema,
    /** O movimento da carteira, o mesmo id das pendências de conciliação. Este objeto é estrito e
     *  um bundle antigo recusaria a página inteira, então só sai para o cliente que o pede
     *  (`?pending=1`), como `reversal_partial`. */
    movementId: z.string().min(1).optional(),
    /** O pagamento ainda espera a nota fiscal (mesmo opt-in de `movementId`). */
    pendingConciliation: z.boolean().optional(),
  })
  .strict();
export type VollWalletStatementEntry = z.infer<typeof VollWalletStatementEntrySchema>;

/**
 * `GET /voll-wallet/statement?category=&kind=&start=&end=&page=` — o extrato de todas as carteiras
 * do viajante, agrupado por dia (São Paulo), uma página por vez. `kind=card` corta no servidor às
 * compras no cartão.
 */
export const VollWalletStatementSchema = z
  .object({
    days: z.array(
      z.object({ date: z.iso.date(), entries: z.array(VollWalletStatementEntrySchema) }).strict(),
    ),
    hasNext: z.boolean(),
  })
  .strict();
export type VollWalletStatement = z.infer<typeof VollWalletStatementSchema>;

/** `GET /voll-wallet/statement/future` — as liberações já agendadas (lançamentos futuros). */
export const VollWalletFutureEntrySchema = z
  .object({
    name: z.string(),
    date: z.iso.date(),
    amount: z.number(),
    currency: z.string().length(3).optional(),
  })
  .strict();
export type VollWalletFutureEntry = z.infer<typeof VollWalletFutureEntrySchema>;

export const VollWalletFutureResponseSchema = z
  .object({ entries: z.array(VollWalletFutureEntrySchema) })
  .strict();
